/**
 * Admin Delete User Modal Component
 *
 * Confirmation modal for deleting a user.
 *
 * @fileoverview Admin user deletion confirmation modal
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  VStack,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react';
import type { AdminUser } from '../../types/admin';

interface AdminDeleteUserModalProps {
  /** Modal open state */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** User to delete */
  user: AdminUser | null;
  /** Confirm callback */
  onConfirm: () => Promise<boolean>;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error?: string | null;
}

/**
 * Admin Delete User Modal Component
 *
 * Confirmation modal for permanently deleting a user.
 * Requires typing the user's email to confirm.
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <AdminDeleteUserModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   user={selectedUser}
 *   onConfirm={handleDelete}
 *   loading={loading}
 * />
 */
export const AdminDeleteUserModal: React.FC<AdminDeleteUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  loading,
  error,
}) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const bgColor = useColorModeValue('white', 'gray.800');

  /**
   * Check if confirmation is valid
   */
  const isConfirmValid = user && confirmEmail === user.email;

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setConfirmEmail('');
    onClose();
  };

  /**
   * Handle confirmation
   */
  const handleConfirm = async () => {
    if (!isConfirmValid) return;

    const success = await onConfirm();
    if (success) {
      setConfirmEmail('');
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Delete User</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              This action is permanent and cannot be undone.
            </Alert>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Text>
              Are you sure you want to permanently delete{' '}
              <Text as="span" fontWeight="bold">
                {user.email}
              </Text>
              ?
            </Text>

            <Text fontSize="sm" color="gray.500">
              This will:
            </Text>
            <VStack align="stretch" pl={4} spacing={1}>
              <Text fontSize="sm" color="gray.500">
                - Remove the user from all tenants
              </Text>
              <Text fontSize="sm" color="gray.500">
                - Delete all user data
              </Text>
              <Text fontSize="sm" color="gray.500">
                - Revoke all access
              </Text>
            </VStack>

            {user.memberships && user.memberships.length > 0 && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                This user is a member of {user.memberships.length} tenant(s).
              </Alert>
            )}

            <FormControl>
              <FormLabel>Confirm by typing the user's email</FormLabel>
              <Input
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
              />
              <FormHelperText>
                Type <Text as="span" fontWeight="bold">{user.email}</Text> to confirm
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleConfirm}
            isLoading={loading}
            isDisabled={!isConfirmValid}
          >
            Delete Permanently
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminDeleteUserModal;
